import React, { useEffect, useRef, useState } from 'react';
import Link from '../components/SafeLink';
import Api from '../services/api';
import { useResource, usePage } from '../hooks/useResource';
import { T, useText, Icon } from '../components/Text';
import { Connection } from '../components/Layout';
import Sessions from '../components/Sessions';

/**
 * Müşteri Yönetimi Sayfası (/customers.html)
 * Müşteri listesi, ekleme/düzenleme, kara liste kontrolü, manuel bildirimler ve oturum bilgileri.
 */
export default function Customers() {
  const { t, locale } = useText();
  usePage(t('Müşteri Yönetimi'), 'admin-page customers-page');

  const resource = useResource('/api/customers', ['customers-changed', 'orders-changed']);
  const customers = resource.data?.customers || [];

  // Arama, filtre ve sıralama
  const [query, setQuery] = useState('');
  const [showCounts, setShowCounts] = useState(false);
  const [ascending, setAscending] = useState(true);

  // Modallar
  const [infoCustomer, setInfoCustomer] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Düzenleme / Ekleme Formu
  const [draft, setDraft] = useState({ name: '', username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [feedback, setFeedback] = useState('');

  // Kara Liste Modalı
  const [blacklistOpen, setBlacklistOpen] = useState(false);
  const [blacklistQuery, setBlacklistQuery] = useState('');
  const [blacklistError, setBlacklistError] = useState('');
  const [blacklistFeedback, setBlacklistFeedback] = useState('');
  const [blacklistPending, setBlacklistPending] = useState(new Set());

  // Manuel Bildirim Modalı
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [recipientId, setRecipientId] = useState(null);
  const [recipientQuery, setRecipientQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const templatesResource = useResource(
    templatesOpen ? '/api/customers/notifications/templates' : null,
    []
  );

  const lock = useRef(false);
  const blacklistLocks = useRef(new Set());

  const formatNumber = (n) => new Intl.NumberFormat(locale).format(n || 0);

  // Arama ve sıralama mantığı
  const filterList = (list, search) =>
    list.filter((c) =>
      [c.name, c.username].some((text) =>
        text.toLocaleLowerCase('tr-TR').includes(search.trim().toLocaleLowerCase('tr-TR'))
      )
    );

  const displayedCustomers = filterList(customers, query).sort(
    (a, b) => a.name.localeCompare(b.name, 'tr') * (ascending ? 1 : -1) || a.id - b.id
  );

  useEffect(() => {
    if (resource.data && infoCustomer && !customers.find((c) => c.id === infoCustomer.id)) {
      setInfoCustomer(null);
    }
  }, [resource.data, customers, infoCustomer]);

  // Yeni Müşteri / Düzenleme Aç
  const handleOpenEdit = (customer = null) => {
    setEditingCustomer(customer);
    setDraft({
      name: customer?.name || '',
      username: customer?.username || '',
      password: '',
    });
    setShowPassword(false);
    setFormError('');
    setEditOpen(true);
  };

  // Müşteri Kaydet (Yeni veya Güncelleme)
  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    if (lock.current) return;

    lock.current = true;
    setBusy(true);
    setFormError('');

    try {
      const url = editingCustomer ? `/api/customers/${editingCustomer.id}` : '/api/customers';
      const method = editingCustomer ? 'PATCH' : 'POST';
      const body = {
        ...draft,
        ...(editingCustomer ? { version: editingCustomer.version } : {}),
      };

      const data = await Api.mutate(url, method, body);
      setEditOpen(false);
      setFeedback(
        data.customer.name +
          (editingCustomer
            ? ' bilgileri güncellendi.'
            : ' müşterisi eklendi. Giriş hesabı hazır.')
      );
      await resource.refresh();
    } catch (failure) {
      setFormError(failure.message);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  // Müşteri Sil
  const handleDeleteCustomer = async () => {
    if (lock.current || !editingCustomer) return;

    lock.current = true;
    setBusy(true);
    setFormError('');

    try {
      const data = await Api.mutate(`/api/customers/${editingCustomer.id}`, 'DELETE', {
        version: editingCustomer.version,
      });
      setDeleteOpen(false);
      setEditOpen(false);
      setFeedback(`${editingCustomer.name} silindi. ${data.unassignedOrders} sipariş müşteri seçimi bekliyor.`);
      await resource.refresh();
    } catch (failure) {
      setFormError(failure.message);
      setDeleteOpen(false);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  // Kara Liste Aç / Kapat
  const handleToggleBlacklist = async (customer) => {
    if (blacklistLocks.current.has(customer.id)) return;

    blacklistLocks.current.add(customer.id);
    setBlacklistPending(new Set(blacklistLocks.current));
    setBlacklistError('');
    setBlacklistFeedback('');

    try {
      const data = await Api.mutate(`/api/customers/${customer.id}/blacklist`, 'PATCH', {
        blacklisted: !customer.blacklisted,
        version: customer.version,
      });
      setBlacklistFeedback(
        data.customer.name +
          (data.customer.blacklisted
            ? ': kara liste açıldı. Otomatik bildirimler durduruldu; hazır siparişleri müşteriye İşlemde gösterilecek.'
            : ': kara liste kapatıldı. Hazır durumları ve otomatik bildirimler yeniden açık.')
      );
    } catch (failure) {
      setBlacklistError(failure.message);
    } finally {
      blacklistLocks.current.delete(customer.id);
      setBlacklistPending(new Set(blacklistLocks.current));
      await resource.refresh();
    }
  };

  // Manuel Bildirim Gönder
  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (lock.current || !recipientId || !selectedTemplate) return;

    lock.current = true;
    setSending(true);
    setSendError('');

    try {
      const payload = {
        customerId: recipientId,
        templateId: selectedTemplate.id,
        requestId: Api.createUploadKey(),
      };
      const data = await Api.mutate('/api/customers/notifications', 'POST', payload);
      setSelectedTemplate(null);
      setTemplatesOpen(false);
      setFeedback(
        `${data.customerName}: “${selectedTemplate.title}” bildirimi ${formatNumber(
          data.accepted
        )} cihaz için iletildi.`
      );
    } catch (failure) {
      setSendError(failure.message);
    } finally {
      lock.current = false;
      setSending(false);
    }
  };

  const blacklistCount = customers.filter((c) => c.blacklisted).length;

  return (
    <main id="main" className="customers-main">
      {/* Üst Başlık ve Hızlı Araçlar */}
      <div className="customers-heading">
        <div>
          <div className="eyebrow">
            <T>MÜŞTERİ YÖNETİMİ</T>
          </div>
          <h1>
            <T>Müşteriler</T>
          </h1>
          <p>
            <T>Firma bilgileri ve giriş hesapları, tek bir yerde.</T>
          </p>
        </div>

        <div className="customers-heading-actions">
          <span className="connection-status">
            <Connection />
          </span>

          <button
            className="customer-tool-button"
            id="open-blacklist"
            type="button"
            onClick={() => {
              setBlacklistOpen(true);
              setBlacklistQuery('');
              setBlacklistError('');
              setBlacklistFeedback('');
            }}
          >
            <T>Kara Liste </T>
            <span id="blacklist-count">{blacklistCount}</span>
          </button>

          <button
            className="customer-tool-button"
            id="open-notifications"
            type="button"
            onClick={() => setTemplatesOpen(true)}
          >
            <T>Manuel Bildirim ↗</T>
          </button>

          <button
            className="button gold"
            id="add-customer"
            type="button"
            disabled={busy}
            onClick={() => handleOpenEdit(null)}
          >
            <T>+ Müşteri Ekle</T>
          </button>
        </div>
      </div>

      {resource.error && (
        <p className="form-error" role="alert">
          <T>{resource.error}</T>
        </p>
      )}

      {feedback && (
        <p className="customer-feedback" id="customer-feedback" role="status">
          <T>{feedback}</T>
        </p>
      )}

      {/* Müşteri Listesi Paneli */}
      <section className="customers-panel" aria-label={t('Müşteri listesi')}>
        <div className="customers-toolbar">
          <label className="customers-search">
            <span aria-hidden="true">⌕</span>
            <input
              id="customer-list-search"
              type="search"
              aria-label={t('Müşteri ara')}
              placeholder={t('Firma veya kullanıcı adı ara…')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>

          <button
            type="button"
            id="show-order-counts"
            aria-pressed={showCounts}
            className={showCounts ? 'selected' : ''}
            onClick={() => setShowCounts(!showCounts)}
          >
            <T>{showCounts ? 'Sipariş sayılarını gizle' : 'Sipariş sayılarını göster'}</T>
          </button>
        </div>

        <div className="customer-results">
          <span id="customer-result-count" role="status">
            <T>{`${formatNumber(displayedCustomers.length)} müşteri`}</T>
          </span>
          <span>
            <T>Girişleri görmek ve düzenlemek için Bilgiler’e tıklayın.</T>
          </span>
        </div>

        <div className="customer-table-wrapper">
          <table className="customer-table">
            <thead>
              <tr>
                <th scope="col">
                  <button
                    id="sort-customers"
                    type="button"
                    onClick={() => setAscending(!ascending)}
                  >
                    <T>{`Müşteri / Firma İsmi ${ascending ? '↓' : '↑'}`}</T>
                  </button>
                </th>
                <th scope="col">
                  <T>Kullanıcı Adı</T>
                </th>
                {showCounts && (
                  <th className="customer-order-count" scope="col">
                    <T>Toplam / Hazır</T>
                  </th>
                )}
                <th scope="col">
                  <T>İşlemler</T>
                </th>
              </tr>
            </thead>
            <tbody id="customer-table-body">
              {displayedCustomers.map((c) => {
                const initials = c.name
                  .split(/\s+/)
                  .map((p) => p[0])
                  .join('')
                  .slice(0, 2)
                  .toLocaleUpperCase('tr-TR');

                return (
                  <tr key={c.id}>
                    <td>
                      <div className="customer-company">
                        <span className="customer-initial">{initials}</span>
                        <span>
                          {c.name}
                          {c.blacklisted && (
                            <>
                              <br />
                              <span className="customer-blacklist-badge">
                                <T>Kara Listede</T>
                              </span>
                            </>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="customer-username">{c.username}</td>
                    {showCounts && (
                      <td className="customer-order-count">
                        {`${formatNumber(c.total)} / ${formatNumber(c.completed)}`}
                      </td>
                    )}
                    <td>
                      <div className="customer-row-actions">
                        <Link to={`/orders?customer=${c.id}`}>
                          <Icon name="list-view" />
                          <T>Siparişler ↗</T>
                        </Link>
                        <button
                          type="button"
                          onClick={() => setInfoCustomer(c)}
                          aria-label={t(`${c.name} bilgileri`)}
                        >
                          <Icon name="info" />
                          <T>Bilgiler ↗</T>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {displayedCustomers.length === 0 && (
          <div className="customers-empty" id="customers-empty">
            <h2>
              <T>Müşteri bulunamadı.</T>
            </h2>
            <p>
              <T>Aramanızı değiştirin veya yeni müşteri ekleyin.</T>
            </p>
          </div>
        )}
      </section>

      {/* 1. Müşteri Bilgileri ve Oturumlar Modalı */}
      {infoCustomer && (
        <div className="dialog-backdrop" onClick={() => setInfoCustomer(null)}>
          <div
            className="customer-dialog customer-tools-dialog open"
            id="customer-info-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="customer-dialog-heading">
              <div>
                <div className="eyebrow">
                  <T>MÜŞTERİ BİLGİLERİ</T>
                </div>
                <h2 id="customer-info-title">{infoCustomer.name}</h2>
              </div>
              <button
                type="button"
                id="close-customer-info"
                onClick={() => setInfoCustomer(null)}
              >
                <T>Kapat ×</T>
              </button>
            </div>

            <div className="customer-info-profile">
              <p id="customer-info-username">
                <T>Kullanıcı adı:</T> {infoCustomer.username}
              </p>
              <button
                type="button"
                id="edit-customer-info"
                onClick={() => {
                  const target = infoCustomer;
                  setInfoCustomer(null);
                  handleOpenEdit(target);
                }}
              >
                <T>Düzenle ↗</T>
              </button>
            </div>

            <div id="customer-sessions">
              <Sessions key={infoCustomer.id} url={`/api/customers/${infoCustomer.id}/sessions`} />
            </div>
          </div>
        </div>
      )}

      {/* 2. Müşteri Ekle / Düzenle Modalı */}
      {editOpen && (
        <div className="dialog-backdrop" onClick={() => !busy && setEditOpen(false)}>
          <div
            className="customer-dialog open"
            id="customer-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="customer-dialog-heading">
              <div>
                <div className="eyebrow">
                  <T>MÜŞTERİ HESABI</T>
                </div>
                <h2 id="customer-dialog-title">
                  <T>{editingCustomer ? 'Müşteriyi Düzenle' : 'Müşteri Ekle'}</T>
                </h2>
              </div>
              <button
                type="button"
                id="close-customer"
                disabled={busy}
                onClick={() => setEditOpen(false)}
              >
                <T>Kapat ×</T>
              </button>
            </div>

            <form id="customer-form" onSubmit={handleSaveCustomer}>
              <label className="field">
                <T>Firma İsmi</T>
                <input
                  name="name"
                  required
                  maxLength={120}
                  autoComplete="off"
                  placeholder={t('Örn: Deneme Kuyumculuk')}
                  value={draft.name}
                  disabled={busy}
                  onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
                />
              </label>

              <label className="field">
                <T>Kullanıcı Adı</T>
                <input
                  name="username"
                  required
                  minLength={3}
                  maxLength={80}
                  pattern="[A-Za-z0-9_.\-]+"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck="false"
                  placeholder={t('Örn: Deneme_Kuyumculuk')}
                  value={draft.username}
                  disabled={busy}
                  onChange={(e) => setDraft((prev) => ({ ...prev, username: e.target.value }))}
                />
              </label>
              <p className="customer-field-help">
                <T>İngilizce harf, rakam, nokta, tire veya alt çizgi kullanın.</T>
              </p>

              <label className="field" htmlFor="customer-password">
                <T>{editingCustomer ? 'Yeni Şifre' : 'Şifre'}</T>
              </label>
              <div className="password-input">
                <input
                  id="customer-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  minLength={6}
                  maxLength={200}
                  autoComplete="new-password"
                  required={!editingCustomer}
                  value={draft.password}
                  disabled={busy}
                  onChange={(e) => setDraft((prev) => ({ ...prev, password: e.target.value }))}
                />
                <button
                  type="button"
                  id="show-customer-password"
                  aria-label={t(showPassword ? 'Şifreyi gizle' : 'Şifreyi göster')}
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <T>{showPassword ? 'Gizle' : 'Göster'}</T>
                </button>
              </div>
              <p className="customer-field-help" id="customer-password-help">
                <T>
                  {editingCustomer
                    ? 'Şifreyi değiştirmeyecekseniz boş bırakın. Yeni şifre girildiğinde müşterinin açık oturumları kapatılır.'
                    : 'En az 6 karakter. Müşteri bu şifreyle giriş yapacak.'}
                </T>
              </p>

              {formError && (
                <p className="form-error" id="customer-form-error" role="alert">
                  <T>{formError}</T>
                </p>
              )}

              <div className="customer-form-actions">
                {editingCustomer && (
                  <button
                    type="button"
                    className="button danger-button"
                    id="delete-customer"
                    disabled={busy}
                    onClick={() => setDeleteOpen(true)}
                  >
                    <T>Müşteriyi Sil</T>
                  </button>
                )}
                <button
                  type="submit"
                  className="button gold"
                  id="save-customer"
                  disabled={busy}
                >
                  <T>{editingCustomer ? 'Değişiklikleri Kaydet ↗' : 'Müşteriyi Ekle ↗'}</T>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 3. Müşteri Silme Onay Modalı */}
      {deleteOpen && (
        <div className="dialog-backdrop" onClick={() => !busy && setDeleteOpen(false)}>
          <div className="customer-dialog open" onClick={(e) => e.stopPropagation()}>
            <div className="customer-dialog-heading">
              <div>
                <div className="eyebrow">
                  <T>DİKKAT</T>
                </div>
                <h2>
                  <T>Müşteriyi Sil</T>
                </h2>
              </div>
            </div>
            <p>
              <T>
                {`${editingCustomer?.name} müşterisinin hesabı silinecek ve açık oturumları kapatılacak. Sipariş ve fotoğrafları korunacak; müşteri atamaları kaldırılıp “Müşteri Seçilmedi” olarak gösterilecek. Bu işlem geri alınamaz.`}
              </T>
            </p>
            <div className="customer-form-actions">
              <button
                type="button"
                className="button outline"
                disabled={busy}
                onClick={() => setDeleteOpen(false)}
              >
                <T>Vazgeç</T>
              </button>
              <button
                type="button"
                className="button danger-button"
                disabled={busy}
                onClick={handleDeleteCustomer}
              >
                <T>{busy ? 'Siliniyor…' : 'Evet, Sil'}</T>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 4. Kara Liste Modalı */}
      {blacklistOpen && (
        <div className="dialog-backdrop" onClick={() => setBlacklistOpen(false)}>
          <div
            className="customer-dialog customer-tools-dialog open"
            id="blacklist-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="customer-dialog-heading">
              <div>
                <div className="eyebrow">
                  <T>MÜŞTERİ KONTROLÜ</T>
                </div>
                <h2 id="blacklist-title">
                  <T>Kara Liste</T>
                </h2>
              </div>
              <button type="button" onClick={() => setBlacklistOpen(false)}>
                <T>Kapat ×</T>
              </button>
            </div>

            <p className="customer-tools-description" id="blacklist-description">
              <T>
                Açıkken otomatik hazır bildirimi gönderilmez ve müşterinin hazır siparişleri “İşlemde”
                görünür. Yönetimde gerçek durum korunur. Kapattığınızda hazır durumları görünür ve
                bekleyen otomatik bildirimler yeniden devreye girer.
              </T>
            </p>

            <label className="customers-search tools-search">
              <input
                id="blacklist-search"
                type="search"
                aria-label={t('Kara listede müşteri ara')}
                placeholder={t('Firma veya kullanıcı adı ara…')}
                value={blacklistQuery}
                onChange={(e) => setBlacklistQuery(e.target.value)}
              />
            </label>

            {blacklistError && (
              <p className="form-error" id="blacklist-error" role="alert">
                <T>{blacklistError}</T>
              </p>
            )}

            {blacklistFeedback && (
              <p className="customer-feedback" id="blacklist-feedback" role="status">
                <T>{blacklistFeedback}</T>
              </p>
            )}

            <div id="blacklist-customers" className="customer-tools-list">
              {filterList(customers, blacklistQuery).map((c) => (
                <div key={c.id} className="customer-control-row">
                  <span className="customer-control-name">
                    <span>{c.name}</span>
                    <small>
                      {c.username}
                      {c.blacklisted && <T> · Kara listede</T>}
                    </small>
                  </span>
                  <button
                    type="button"
                    className="blacklist-switch"
                    role="switch"
                    aria-checked={c.blacklisted}
                    disabled={blacklistPending.has(c.id)}
                    aria-label={t(`${c.name} kara liste`)}
                    onClick={() => handleToggleBlacklist(c)}
                  >
                    <span className="blacklist-switch-track" aria-hidden="true" />
                    <span>
                      <T>{c.blacklisted ? 'Açık' : 'Kapalı'}</T>
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 5. Manuel Bildirim Şablonları & Alıcı Seçimi Modalı */}
      {templatesOpen && (
        <div
          className="dialog-backdrop"
          onClick={() => !sending && (selectedTemplate ? setSelectedTemplate(null) : setTemplatesOpen(false))}
        >
          <div
            className="customer-dialog customer-tools-dialog open"
            id="notifications-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="customer-dialog-heading">
              <div>
                <div className="eyebrow">
                  <T>MÜŞTERİYE MESAJ</T>
                </div>
                <h2 id="notifications-title">
                  <T>Manuel Bildirim</T>
                </h2>
              </div>
              <button
                type="button"
                disabled={sending}
                onClick={() => {
                  setSelectedTemplate(null);
                  setTemplatesOpen(false);
                }}
              >
                <T>Kapat ×</T>
              </button>
            </div>

            {!selectedTemplate ? (
              <>
                <p className="customer-tools-description">
                  <T>
                    Mesajın yanındaki Gönder’e basın, ardından müşteriyi seçin. Kara listedeki
                    müşterilere de manuel bildirim gönderebilirsiniz; sipariş durumları değişmez.
                  </T>
                </p>

                {templatesResource.error && (
                  <p className="form-error" role="alert">
                    <T>{templatesResource.error}</T>
                  </p>
                )}

                <div id="notification-templates" className="notification-templates">
                  {templatesResource.data?.templates.map((tpl) => (
                    <article className="notification-template" key={tpl.id}>
                      <h3>
                        <T>{tpl.title}</T>
                      </h3>
                      <p>
                        <T>{tpl.body}</T>
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedTemplate(tpl);
                          setRecipientId(null);
                          setRecipientQuery('');
                          setSendError('');
                        }}
                      >
                        <T>Gönder ↗</T>
                      </button>
                    </article>
                  ))}
                </div>
              </>
            ) : (
              <form onSubmit={handleSendNotification}>
                <div className="notification-selected-preview">
                  <h3>
                    <T>{selectedTemplate.title}</T>
                  </h3>
                  <p>
                    <T>{selectedTemplate.body}</T>
                  </p>
                </div>

                <label className="customers-search tools-search">
                  <input
                    type="search"
                    placeholder={t('Alıcı müşteri ara…')}
                    value={recipientQuery}
                    disabled={sending}
                    onChange={(e) => setRecipientQuery(e.target.value)}
                  />
                </label>

                {sendError && (
                  <p className="form-error" role="alert">
                    <T>{sendError}</T>
                  </p>
                )}

                <div className="customer-tools-list notification-recipients-list">
                  {filterList(customers, recipientQuery).map((c) => (
                    <label
                      key={c.id}
                      className={`customer-control-row notification-recipient ${
                        recipientId === c.id ? 'selected' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="recipient"
                        value={c.id}
                        checked={recipientId === c.id}
                        disabled={sending}
                        onChange={() => {
                          setRecipientId(c.id);
                          setSendError('');
                        }}
                      />
                      <span className="customer-control-name">
                        <span>{c.name}</span>
                        <small>{c.username}</small>
                      </span>
                    </label>
                  ))}
                </div>

                <div className="customer-form-actions">
                  <button
                    type="button"
                    className="button outline"
                    disabled={sending}
                    onClick={() => setSelectedTemplate(null)}
                  >
                    <T>Geri Dön</T>
                  </button>
                  <button
                    type="submit"
                    className="button gold"
                    disabled={!recipientId || sending}
                  >
                    <T>{sending ? 'Gönderiliyor…' : 'Bildirimi Gönder ↗'}</T>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
